import React, { useRef, useState, useEffect, useCallback } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Box, 
  Button, 
  IconButton, 
  Badge, 
  Avatar, 
  Menu, 
  MenuItem,
  Divider,
  useMediaQuery,
  useTheme,
  Popover,
  Chip,
  Dialog,
  DialogContent,
  DialogActions,
  Stack,
  Snackbar,
  Alert,
} from '@mui/material';
import {
  HelpOutline,
  PhoneIphoneOutlined,
  NotificationsOutlined,
  Language,
  Shield,
  Logout,
  KeyboardArrowLeft,
  KeyboardArrowRight,
  Info,
  SupportAgent,
  MenuBook,
  Comment,
  RocketLaunchOutlined,
  CloseOutlined,
  CheckCircleOutlined,
  WarningAmberOutlined,
  ErrorOutlined,
  AccessTimeOutlined,
  SwapHorizOutlined,
} from '@mui/icons-material';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
  PRODUCT_NAME, logoForMark, tenantMarkFor,
} from '../../branding';
import {
  GUTTER, HEADER_HEIGHT, LOGO_HEIGHT, LOGO_ZONE, NAV_ORIGIN,
} from '../../theme/shellMetrics';
import { useAuth } from '../../auth/AuthContext';
import { useRoleLanding } from '../../hooks/useRoleLanding';
import { clearCurrentOrgId, onboardingApi, subscriptionApi } from '../../api';
import { meApi } from '../../api/me';
import type { Plan, Subscription } from '../../api/subscription';
import OrgSwitcher from './OrgSwitcher';
import GetAppDialog from '../face/GetAppDialog';
import SupportCaseDialog, {
  openSupportCase,
} from '../support/SupportCaseDialog';
import PlanPicker from '../billing/PlanPicker';
import ChangePasswordDialog from '../security/ChangePasswordDialog';
import {
  isCanonicalPlan,
  sortPlansByTier,
} from '../billing/planMeta';
import { adminNotificationTarget } from './notificationTarget';
import { useTranslation } from 'react-i18next';
import { activeLocale } from '../../utils/datetime';
import {
  SUPPORTED_LOCALES,
  LOCALE_NAMES,
  RTL_LOCALES,
  type SupportedLocale,
} from '../../i18n/config';

import { localizeProcessNotificationArgs } from '../../pages/Processes/processDisplay';

// Common font family style to be used throughout the component
const fontFamilyStyle = {
  fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  fontWeight: 500
};

// Typography style for menu items
const typographyStyle = {
  ...fontFamilyStyle,
  lineHeight: 1.2,
  color: 'text.primary',
};

// Navigation button style
const navButtonStyle = {
  mx: 0.5,
  height: 36,
  px: 2,
  minWidth: 'auto',
  textTransform: 'none',
  fontSize: '0.875rem',
  borderRadius: '6px',
  whiteSpace: 'nowrap',
  ...fontFamilyStyle,
  '&:hover': {
    bgcolor: 'rgba(0, 0, 0, 0.04)',
  },
};

interface TopNavProps {
  activeSection: string;
  onSectionChange: (section: string) => void;
  licensedProducts?: string[];
}

const ALL_MENU_ITEMS = [
  'Dashboard',
  'Organization',
  'Processes',
  'Attendance',
  'Integrations',
  'System'
];

// i18n: map menu IDs to translation keys
const MENU_ITEM_KEYS: Record<string, string> = {
  Dashboard: 'common:nav.dashboard',
  Organization: 'common:nav.organization',
  Processes: 'common:nav.processes',
  Attendance: 'common:nav.attendance',
  Integrations: 'common:nav.connectors',
  System: 'common:nav.system',
};

const menuPaperProps = {
  elevation: 0,
  sx: {
    mt: 1.5,
    minWidth: '200px',
    overflow: 'visible',
    border: '1px solid',
    borderColor: 'rgba(0, 0, 0, 0.2)',
    borderRadius: 2,
    bgcolor: 'background.paper',
    ...fontFamilyStyle,
    '&:before': {
      content: '""',
      display: 'block',
      position: 'absolute',
      top: 0,
      insetInlineEnd: 14,
      width: 10,
      height: 10,
      bgcolor: 'background.paper',
      transform: 'translateY(-50%) rotate(45deg)',
      zIndex: 0,
      border: '1px solid',
      borderColor: 'rgba(0, 0, 0, 0.2)',
      borderBottom: 'none',
      borderRight: 'none'
    },
  }
};

const menuItemStyle = {
  py: 1,
  px: 2,
  ...fontFamilyStyle,
  '&:hover': {
    bgcolor: 'primary.lighter',
  },
  '& .MuiSvgIcon-root': {
    fontSize: '20px',
    color: 'text.secondary',
    mr: 1.5,
  }
};

// Small elegant scroll arrow — restored from original 24×24
// minimal design (owner: '太显眼了'). The arrow is a subtle
// affordance, not a heavy button. Easier to surface via tighter
// inner Box padding (so overflow shows the arrow with less work).
const scrollButtonStyle = {
  position: 'absolute',
  zIndex: 2,
  top: '50%',
  transform: 'translateY(-50%)',
  bgcolor: 'rgba(255, 255, 255, 0.95)',
  width: 24,
  height: 24,
  borderRadius: '50%',
  border: '1px solid rgba(0,0,0,0.08)',
  color: 'text.secondary',
  '&:hover': {
    bgcolor: '#ffffff',
    borderColor: 'rgba(0,0,0,0.15)',
    color: 'text.primary',
  },
};

const TopNav: React.FC<TopNavProps> = ({ activeSection, onSectionChange, licensedProducts }) => {
  const theme = useTheme();
  const { t, i18n } = useTranslation();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const mainMenuItems = ALL_MENU_ITEMS.filter((item) => {
    if (item === 'Attendance' && licensedProducts && !licensedProducts.includes('qutime')) return false;
    if (item === 'Processes' && licensedProducts && !licensedProducts.includes('pureoa')) return false;
    return true;
  });
  const { signOut, user } = useAuth();
  const { securityPath } = useRoleLanding();
  
  // Derive display values from user profile
  const displayName = user?.full_name || user?.username || user?.email?.split('@')[0] || '';
  const displayEmail = user?.email || '';
  const avatarInitials = displayName
    ? displayName.split(' ').map((n) => n[0]).join('').toUpperCase().slice(0, 2)
    : displayEmail ? displayEmail[0].toUpperCase() : '?';

  const [userMenuAnchor, setUserMenuAnchor] = useState<null | HTMLElement>(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [helpMenuAnchor, setHelpMenuAnchor] = useState<null | HTMLElement>(null);
  // P1.5a-3 — Language switcher stub. The admin SPA isn't translated
  // yet (all UI strings hardcoded English in JSX). Switcher is wired
  // so the UX surface exists + a user can register intent ("I want
  // Español"); once translation files land we'll persist the choice
  // and React-i18next will swap resources without further chrome
  // changes.
  const [languageMenuAnchor, setLanguageMenuAnchor] = useState<null | HTMLElement>(null);
  const [languageToast, setLanguageToast] = useState<string | null>(null);
  const [notifAnchor, setNotifAnchor] = useState<null | HTMLElement>(null);
  const [notifications, setNotifications] = useState<{
    id: string; type: string; title: string; desc: string; time: string; read: boolean; link: string | null;
  }[]>([]);
  const unreadCount = notifications.filter(n => !n.read).length;

  // Fetch notifications from backend
  useEffect(() => {
    import('../../api/client').then(({ default: api }) => {
      api.get('/v1/notifications').then((r: any) => {
        const items = (r.data ?? []).map((n: any) => ({
          id: n.id,
          type: n.type ?? 'info',
          title: n.title_key ? t(n.title_key, { ...localizeProcessNotificationArgs(t, n.i18n_args), defaultValue: n.title }) : n.title,
          desc: n.body_key ? t(n.body_key, { ...localizeProcessNotificationArgs(t, n.i18n_args), defaultValue: n.body ?? '' }) : (n.body ?? ''),
          time: n.created_at ? new Date(n.created_at).toLocaleString(activeLocale()) : '',
          read: n.read ?? false,
          link: n.link ?? null,
        }));
        setNotifications(items);
      }).catch(() => {});
    });
  }, [t]);

  // Poll every 60s for new notifications
  useEffect(() => {
    const interval = setInterval(() => {
      import('../../api/client').then(({ default: api }) => {
        api.get('/v1/notifications').then((r: any) => {
          const items = (r.data ?? []).map((n: any) => ({
            id: n.id,
            type: n.type ?? 'info',
            title: n.title_key ? t(n.title_key, { ...localizeProcessNotificationArgs(t, n.i18n_args), defaultValue: n.title }) : n.title,
            desc: n.body_key ? t(n.body_key, { ...localizeProcessNotificationArgs(t, n.i18n_args), defaultValue: n.body ?? '' }) : (n.body ?? ''),
            time: n.created_at ? new Date(n.created_at).toLocaleString(activeLocale()) : '',
            read: n.read ?? false,
            link: n.link ?? null,
          }));
          setNotifications(items);
        }).catch(() => {});
      });
    }, 60000);
    return () => clearInterval(interval);
  }, [t]);

  const markAllRead = () => {
    import('../../api/client').then(({ default: api }) => {
      api.post('/v1/notifications/mark-all-read').then(() => {
        setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      }).catch(() => {});
    });
  };
  const markRead = (id: string) => {
    import('../../api/client').then(({ default: api }) => {
      api.post(`/v1/notifications/${id}/read`).then(() => {
        setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      }).catch(() => {});
    });
  };
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [getAppOpen, setGetAppOpen] = useState(false);
  const [changePasswordOpen, setChangePasswordOpen] = useState(false);
  const [versionInfo, setVersionInfo] = useState<{
    commit?: string; tag?: string; built_at?: string; service?: string;
  } | null>(null);
  // Plans + current subscription for the Upgrade dialog. Fetched the
  // first time the dialog opens (not on mount) so the TopNav doesn't
  // pay for this data on every page load. Kept in state so a second
  // open reuses the already-fetched values.
  const [plans, setPlans] = useState<Plan[] | null>(null);
  const [currentSub, setCurrentSub] = useState<Subscription | null>(null);
  // Product line tab inside Upgrade dialog. Default to 'bundle' since
  // it's the cross-sell SKU (matches /pricing default tab).
  const [upgradeProductLine] = useState<'qutime' | 'pureoa' | 'bundle'>('bundle');

  // Which mark this TENANT gets. Derived from the licence rather than from the
  // build's brand: `LOGO` / `PRODUCT_NAME` come from REACT_APP_PRODUCT, which is
  // fixed per deployment, so a PureOA-only org on the QuTime build was shown
  // QuTime's mark. An org with neither licence falls back to the build's own brand,
  // which is the honest answer when there is nothing to derive from.
  const tenantMark = tenantMarkFor(licensedProducts);
  const tenantLogo = logoForMark(tenantMark);
  const tenantLogoAlt = tenantMark === 'pureoa'
    ? 'PureOA'
    : tenantMark === 'qutime' ? 'QuTime' : `${PRODUCT_NAME} Logo`;
  const [plansLoading, setPlansLoading] = useState(false);
  const [plansError, setPlansError] = useState<string | null>(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  
  // Check if scrolling is possible and in which direction
  const checkScrollPosition = useCallback(() => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      setCanScrollLeft(scrollLeft > 0);
      // Add a small buffer (1px) to account for rounding errors
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth - 1);
    }
  }, []);

  // Hydration-safe scroll detection via ResizeObserver.
  //
  // Previously we ran the check only on mount + window resize, which
  // missed hard-refresh: fonts hadn't loaded → menu items narrow →
  // no overflow detected → arrow never surfaced → 'System' clipped
  // with no recovery path.
  //
  // ResizeObserver subsumes every trigger we care about:
  //   - container size changes (window resize, sidebar collapse, parent reflow)
  //   - child size changes (font load widens text, locale switch, dynamic content)
  // so window 'resize' / fonts.ready / setTimeout fallbacks are
  // redundant and removed.
  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') {
      checkScrollPosition();
      return;
    }
    const observer = new ResizeObserver(() => checkScrollPosition());
    observer.observe(container);
    Array.from(container.children).forEach((child) => observer.observe(child));
    return () => observer.disconnect();
  }, [checkScrollPosition]);

  // Listen for app-wide 'svdy:upgrade:open' custom events so any
  // surface (PlanFeatureBanner, SSO settings, Integrations connect
  // dialog, etc.) can trigger the Upgrade dialog by dispatching
  // a single window event. Pre-fix the event was being dispatched
  // from 2 sites (PlanFeatureBanner / SSO) but no listener existed
  // — clicks were silent (草台 bug sweep, Bug 5 root cause).
  useEffect(() => {
    const handler = () => setUpgradeOpen(true);
    window.addEventListener('svdy:upgrade:open', handler);
    return () => window.removeEventListener('svdy:upgrade:open', handler);
  }, []);

  // Fetch version info when About dialog opens
  useEffect(() => {
    if (!aboutOpen) return;
    import('../../api/client').then(({ default: api }) => {
      // C3 (HANDOFF_2026-05-16 Addendum 10): the version endpoint
      // moved from /v1/public/version to /v1/version (JWT-gated).
      // The About dialog only renders inside the admin shell so we
      // already have a token in the axios client; no anonymous use
      // case lost.
      api.get('/v1/version').then(r => setVersionInfo(r.data)).catch(() => {});
    });
  }, [aboutOpen]);

  // H (HANDOFF_2026-05-16 Addendum 13): read org.whitelabel from
  // the profile endpoint to suppress SVDY-specific About content
  // for white-label-eligible tenants. Lazy-fetched on first dialog
  // open. Default false so legacy fetches before the response
  // lands keep showing the SVDY card (safer to show than to hide).
  const [whitelabel, setWhitelabel] = useState(false);
  useEffect(() => {
    if (!aboutOpen) return;
    if (whitelabel) return;  // already fetched / true
    import('../../api/organization').then(({ profileApi }) => {
      profileApi.get()
        .then(p => setWhitelabel(Boolean(p.whitelabel)))
        .catch(() => {});
    });
  }, [aboutOpen, whitelabel]);

  // Lazy-fetch plans + current subscription when the Upgrade dialog
  // first opens. Subsequent opens reuse the cached values unless the
  // user explicitly retries after an error.
  useEffect(() => {
    if (!upgradeOpen) return;
    if (plans !== null && !plansError) return; // already loaded
    let cancelled = false;
    (async () => {
      setPlansLoading(true);
      setPlansError(null);
      try {
        const [plansR, currentR] = await Promise.allSettled([
          subscriptionApi.plansApi.list(),
          subscriptionApi.currentApi.get(),
        ]);
        if (cancelled) return;
        if (plansR.status === 'fulfilled') {
          // Single source of truth: canonical filter + tier-order sort.
          // (Helpers in components/billing/planMeta.)
          setPlans(sortPlansByTier(plansR.value.filter(isCanonicalPlan)));
        } else {
          setPlansError(t('common:feedback.errorGeneric'));
        }
        // Current subscription is a nice-to-have; show the "Current
        // Plan" badge if we have it, plain cards otherwise.
        if (currentR.status === 'fulfilled') {
          setCurrentSub(currentR.value);
        }
      } finally {
        if (!cancelled) setPlansLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [upgradeOpen, plans, plansError, t]);
  
  const handleScroll = useCallback((direction: 'left' | 'right') => {
    if (scrollContainerRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = scrollContainerRef.current;
      const maxScroll = scrollWidth - clientWidth;
      
      // Use responsive scroll amount based on screen width
      const baseScrollAmount = isMobile ? clientWidth * 0.7 : 200;
      
      let scrollAmount: number;
      if (direction === 'right') {
        scrollAmount = Math.min(baseScrollAmount, maxScroll - scrollLeft);
      } else {
        scrollAmount = Math.min(baseScrollAmount, scrollLeft);
      }
      
      scrollContainerRef.current.scrollTo({
        left: direction === 'left' ? scrollLeft - scrollAmount : scrollLeft + scrollAmount,
        behavior: 'smooth'
      });
      
      setTimeout(checkScrollPosition, 400);
    }
  }, [checkScrollPosition, isMobile]);
  
  const handleSectionClick = (section: string) => {
    onSectionChange(section);
  };
  
  const handleUserMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setUserMenuAnchor(event.currentTarget);
  };
  
  const handleUserMenuClose = () => {
    setUserMenuAnchor(null);
  };
  
  const handleHelpMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setHelpMenuAnchor(event.currentTarget);
  };
  
  const handleHelpMenuClose = () => {
    setHelpMenuAnchor(null);
  };
  
  const handleSignOut = () => {
    handleUserMenuClose();
    signOut();
  };

  /**
   * Switch organization flow. Clears the cached current_organization_id
   * and hard-navigates to OrgGate. We intentionally use
   * `window.location.assign` instead of react-router `navigate` here:
   *
   *   - The Menu container has `onClick={handleUserMenuClose}` to
   *     auto-close on any MenuItem click. That bubbles after this
   *     handler runs and can race with a soft navigation.
   *   - A full page load also flushes any in-memory state that was
   *     scoped to the old org, which is exactly the user's intent when
   *     they click 'Switch organization'.
   *
   * OrgGate will then re-run GET /me/organizations and:
   *   - 0 orgs → show create-form
   *   - 1 org  → auto-pick (silent, harmless for single-org users)
   *   - 2+ orgs → show the card picker the user actually wants.
   */
  const handleSwitchOrg = () => {
    handleUserMenuClose();
    clearCurrentOrgId();
    window.location.assign('/auth/select-org');
  };

  return (
    <AppBar 
      position="fixed"
      color="default" 
      elevation={0}
      sx={{ 
        border: '1px solid',
        borderColor: 'rgba(0, 0, 0, 0.2)',
        borderTop: 0,
        borderInlineStart: 0,
        borderRight: 0,
        bgcolor: 'white',
        zIndex: theme.zIndex.drawer + 1,
        ...fontFamilyStyle
      }}
    >
      <Toolbar 
        disableGutters
        sx={{ 
          minHeight: `${HEADER_HEIGHT}px`,
          px: 0,
          display: 'flex',
          position: 'relative',
          ...fontFamilyStyle
        }}
      >
        {/* Logo block — exactly the expanded rail's width, with the logo LEFT-ALIGNED
            at the gutter rather than centred.
            Centring made the left edge `(240 − renderedLogoWidth) / 2`, so it floated
            with the logo's intrinsic width and a bundle tenant saw the header start in
            a different place than a single-product tenant. Left-aligned at GUTTER it is
            one chosen number, shared with the rail's labels directly below it and with
            the content on rail-less routes (owner review 2026-08-01). */}
        <Box 
          sx={{
            // Exactly the widest mark plus its gutter, not the expanded rail's width.
            // 240 was option A's rule (`docs/audits/SHELL_ALIGNMENT_2026-08-01.md`)
            // applied on top of option C's logo rule, which left 104.6px of dead space
            // and pushed the nav past the 13" baseline in French. The audit
            // recommended C; this completes it.
            width: GUTTER + LOGO_ZONE,
            minWidth: GUTTER + LOGO_ZONE,
            height: HEADER_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-start',
            pl: `${GUTTER}px`,
            bgcolor: 'white',
          }}
        >
          {/* ONE mark, one height, for every tenant.
              The mark follows the TENANT's licence rather than the build's brand:
              `LOGO` resolves from REACT_APP_PRODUCT, fixed per deployment, so a
              PureOA-only org was shown QuTime's wordmark on the QuTime build — which
              a multi-org admin hits the moment they switch licences.

              A bundle tenant gets the COMPANY mark. It replaces a side-by-side pair
              that ADR-031 Phase 2b called temporary "until a dedicated bundle mark",
              and the numbers made it necessary rather than merely tidier: at
              `maxWidth: 90` per half the clamp silently overrode `height`, so the two
              wordmarks rendered 18.6px and 19.8px tall — unequal to each other and a
              third smaller than every single-licence tenant's — beneath a 22px divider
              that stood taller than both. Two wordmarks cannot exceed 20px in the
              208px this rail leaves, against 28px for one. "You have both products" is
              already carried, better, by Attendance AND Processes both appearing in the
              nav. */}
          <Box
            component="img"
            src={tenantLogo}
            alt={tenantLogoAlt}
            sx={{
              height: LOGO_HEIGHT,
              // Above the widest mark at this height (qutime is 135px), so it never
              // clamps — a width cap that bites overrides `height` through
              // objectFit and hides the fact that it did.
              maxWidth: 160,
              objectFit: 'contain',
            }}
          />
        </Box>

        {/* Scrollable Navigation Container */}
        <Box sx={{ 
          display: 'flex', 
          alignItems: 'center', 
          flex: 1,
          position: 'relative',
          minWidth: 0,
          height: HEADER_HEIGHT,
          ...fontFamilyStyle
        }}>
          {canScrollLeft && (
            <IconButton
              size="small"
              onClick={() => handleScroll('left')}
              sx={{
                ...scrollButtonStyle,
                left: 6,
              }}
            >
              <KeyboardArrowLeft fontSize="small" />
            </IconButton>
          )}

          <Box
            ref={scrollContainerRef}
            sx={{
              display: 'flex',
              overflowX: 'hidden',
              flex: 1,
              // One gutter after the logo zone, so the first nav item lands on
              // NAV_ORIGIN (= GUTTER + LOGO_ZONE + GUTTER = 200). Constant: it used to
              // be 12, and 36 once the overflow arrow appeared, so adding a nav item
              // or switching to a longer locale shifted every label by 24px.
              //
              // It is deliberately NOT aligned with the page title. That alignment
              // only ever held while the rail was expanded — never on /dashboard,
              // which is rail-less and the most-visited page — and buying it cost the
              // dead space and the French overflow. Stripe, Vercel, GitHub and Notion
              // all decline the same alignment.
              pl: `${NAV_ORIGIN - GUTTER - LOGO_ZONE}px`,
              pr: `${GUTTER}px`,
              transition: 'padding 0.2s ease',
              scrollBehavior: 'smooth',
              scrollbarWidth: 'none',
              msOverflowStyle: 'none',
              '&::-webkit-scrollbar': { display: 'none' },
              maskImage: 'linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)',
              WebkitMaskImage: 'linear-gradient(to right, transparent, black 16px, black calc(100% - 16px), transparent)',
              ...fontFamilyStyle
            }}
            onScroll={checkScrollPosition}
          >
            {mainMenuItems.map((item) => (
              <Button
                key={item}
                component={Link}
                to={`/${item.toLowerCase()}`}
                color={activeSection === item ? 'primary' : 'inherit'}
                sx={{
                  ...navButtonStyle,
                  ...(activeSection === item && {
                    bgcolor: 'primary.lighter',
                    fontWeight: 600,
                    '&:hover': {
                      bgcolor: 'primary.lighter',
                    }
                  })
                }}
                onClick={() => handleSectionClick(item)}
              >
                {t(MENU_ITEM_KEYS[item] || item)}
              </Button>
            ))}
          </Box>

          {canScrollRight && (
            <IconButton
              size="small"
              onClick={() => handleScroll('right')}
              sx={{
                ...scrollButtonStyle,
                right: 6,
              }}
            >
              <KeyboardArrowRight fontSize="small" />
            </IconButton>
          )}
        </Box>

        {/* Right Navigation */}
        <Box 
          sx={{ 
            display: 'flex', 
            alignItems: 'center',
            height: HEADER_HEIGHT,
            // pl 3 (24px) > pr 2 (16px) so the right area is clearly
            // separated from the scrollable nav's right edge — fixes
            // the visual overlap owner saw between the right scroll
            // arrow (sitting at outer Box's right edge) and the
            // Upgrade button (which used to sit 16px past the seam,
            // now 24px past, giving a clear visual gap).
            pl: 3,
            // Symmetric with the band's left inset. It was 16 while the left content
            // sat at ~24, so the header was visibly lopsided at narrow widths.
            pr: `${GUTTER}px`,
            ...fontFamilyStyle
          }}
        >
          {/* Upgrade Button - Hide on small screens */}
          {!isMobile && (
            <Button
              variant="contained"
              size="small"
              onClick={() => setUpgradeOpen(true)}
              startIcon={
                <RocketLaunchOutlined sx={{ fontSize: '1rem' }} />
              }
              sx={{
                mr: 2,
                ...fontFamilyStyle,
                display: { xs: 'none', sm: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 0.5,
                background: 'linear-gradient(135deg, #2E65D4 0%, #1E54C0 100%)',
                border: '1px solid rgba(255,255,255,0.15)',
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '0.8rem',
                fontWeight: 600,
                px: 2,
                whiteSpace: 'nowrap',
                '&:hover': { background: 'linear-gradient(135deg, #1E54C0 0%, #163D8F 100%)' },
              }}
            >
              {t('common:topNav.upgrade')}
            </Button>
          )}
          
          {/* Help & Support */}
          <IconButton
            size="small"
            sx={{ 
              mx: { xs: 0.5, sm: 1 },
              '&:hover': { 
                bgcolor: 'primary.lighter'
              }
            }}
            onClick={handleHelpMenuOpen}
          >
            <HelpOutline fontSize="small" />
          </IconButton>
          <Menu
            anchorEl={helpMenuAnchor}
            open={Boolean(helpMenuAnchor)}
            onClose={handleHelpMenuClose}
            onClick={handleHelpMenuClose}
            PaperProps={menuPaperProps}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <MenuItem sx={menuItemStyle} onClick={async () => {
              handleHelpMenuClose();
              // ADR-019 §D8 — backend-driven reset clears server-side
              // dismissed flags so manually-dismissed steps come back
              // for everyone in the org. Per-device localStorage card
              // dismissals are wiped here so the card itself reappears
              // in this browser too. Fire-and-forget on failure;
              // dispatching the event still lets per-page reactivation
              // work locally.
              try { await onboardingApi.reset(); } catch (e) { console.warn('onboarding reset failed', e); }
              ['setup-guide-dismissed', 'org-setup-guide-dismissed', 'dashboard-tour-dismissed',
               'att-setup-guide-dismissed', 'integrations-setup-guide-dismissed', 'sys-setup-guide-dismissed',
               'setup-guide-card-dismissed:attendance', 'setup-guide-card-dismissed:organization',
               'setup-guide-card-dismissed:system', 'setup-guide-card-dismissed:integrations',
               'setup-guide-card-dismissed:dashboard',
              ].forEach((k) => localStorage.removeItem(k));
              window.dispatchEvent(new CustomEvent('show-setup-guide'));
              const p = location.pathname;
              if (!p.startsWith('/organization') && !p.startsWith('/processes') && !p.startsWith('/dashboard')
                  && !p.startsWith('/attendance') && !p.startsWith('/integrations') && !p.startsWith('/system')) {
                navigate('/processes/overview');
              }
            }}>
              <RocketLaunchOutlined />
              <Typography variant="body2" sx={typographyStyle}>{t('common:nav.setupGuide', { defaultValue: 'Setup Guide' })}</Typography>
            </MenuItem>
            <MenuItem sx={menuItemStyle} onClick={() => setGetAppOpen(true)}>
              <PhoneIphoneOutlined />
              <Typography variant="body2" sx={typographyStyle}>{t('common:nav.getMobileApp')}</Typography>
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem sx={menuItemStyle} onClick={() => openSupportCase()}>
              <SupportAgent />
              <Typography variant="body2" sx={typographyStyle}>
                {t('common:topNav.submitSupportCase')}
              </Typography>
            </MenuItem>
            {/* TODO: Documentation hub planned but not yet built.
                Hidden until /docs lands. Re-enable by removing the
                surrounding fragment + uncomment-and-link to the
                docs site once content exists. */}
            {false && (
              <MenuItem sx={menuItemStyle}>
                <MenuBook />
                <Typography variant="body2" sx={typographyStyle}>{t('common:nav.documentation', { defaultValue: 'Documentation' })}</Typography>
              </MenuItem>
            )}
            <MenuItem sx={menuItemStyle} onClick={() => setAboutOpen(true)}>
              <Info />
              <Typography variant="body2" sx={typographyStyle}>{t('common:nav.about', { defaultValue: 'About' })}</Typography>
            </MenuItem>
            <Divider sx={{ my: 1 }} />
            <MenuItem sx={menuItemStyle} onClick={() => openSupportCase({ category: 'feature', subject: '', description: '' })}>
              <Comment />
              <Typography variant="body2" sx={typographyStyle}>{t('common:nav.sendFeedback', { defaultValue: 'Send Feedback' })}</Typography>
            </MenuItem>
          </Menu>
          
          {/* Org switcher — always-visible workspace chip. Shows the
              active org name and opens a picker popover to switch. */}
          <OrgSwitcher />

          {/* Notifications */}
          <IconButton
            size="small"
            onClick={e => setNotifAnchor(e.currentTarget)}
            sx={{ mx: { xs: 0.5, sm: 1 } }}
          >
            <Badge 
              badgeContent={unreadCount} 
              color="error"
              sx={{
                '& .MuiBadge-badge': {
                  fontSize: '0.65rem',
                  minWidth: '16px',
                  height: '16px',
                  padding: '0 4px'
                }
              }}
            >
              <NotificationsOutlined fontSize="small" />
            </Badge>
          </IconButton>
          <Popover
            open={!!notifAnchor}
            anchorEl={notifAnchor}
            onClose={() => setNotifAnchor(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{ elevation: 0, sx: {
              width: 380, maxHeight: 480, borderRadius: 2, mt: 1.5,
              border: '1px solid', borderColor: 'rgba(0,0,0,0.2)', overflow: 'visible',
              '&:before': {
                content: '""', display: 'block', position: 'absolute', top: 0, insetInlineEnd: 14,
                width: 10, height: 10, bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)', zIndex: 0,
                border: '1px solid', borderColor: 'rgba(0,0,0,0.2)', borderBottom: 'none', borderRight: 'none',
              },
            } }}
          >
            <Box sx={{ px: 2, py: 1.5, display: 'flex', justifyContent: 'space-between', alignItems: 'center', borderBottom: '1px solid', borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, fontSize: '0.9rem', color: 'text.primary' }}>{t('common:topNav.notifications')}</Typography>
                {unreadCount > 0 && <Chip label={unreadCount} size="small" color="error" sx={{ height: 18, fontSize: '0.65rem' }} />}
              </Box>
              {unreadCount > 0 && (
                <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }} onClick={markAllRead}>
                  {t('common:topNav.markAllRead')}
                </Typography>
              )}
            </Box>
            <Box sx={{ overflowY: 'auto', maxHeight: 380, overflowX: 'hidden' }}>
              {notifications.map(n => (
                <Box key={n.id} onClick={() => {
                  // Mark read locally + on server, then navigate if a
                  // deep-link was attached. P2.7: alembic 013 added
                  // notifications.link; backend writers populate it
                  // for billing + support flows. Click without link
                  // just dismisses + marks read.
                  markRead(n.id);
                  setNotifAnchor(null);
                  if (n.link) {
                    navigate(adminNotificationTarget(n.link));
                  }
                }}
                  sx={{
                    px: 2, py: 1.25, cursor: 'pointer', display: 'flex', gap: 1.5,
                    bgcolor: n.read ? 'transparent' : 'primary.light',
                    borderBottom: '1px solid', borderColor: 'divider',
                    transition: 'background-color 80ms ease',
                    '&:hover': { bgcolor: n.read ? 'grey.50' : 'primary.light' },
                  }}>
                  <Box sx={{ mt: 0.25 }}>
                    {n.type === 'success' && <CheckCircleOutlined sx={{ fontSize: 18, color: 'success.main' }} />}
                    {n.type === 'alert' && <WarningAmberOutlined sx={{ fontSize: 18, color: 'error.main' }} />}
                    {n.type === 'approval' && <AccessTimeOutlined sx={{ fontSize: 18, color: 'warning.main' }} />}
                    {n.type === 'info' && <ErrorOutlined sx={{ fontSize: 18, color: 'primary.main' }} />}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography variant="caption" sx={{ fontWeight: n.read ? 500 : 700, fontSize: '0.75rem', color: 'text.primary', display: 'block' }}>{n.title}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem', display: 'block', mt: 0.25 }}>{n.desc}</Typography>
                    <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem', mt: 0.5, display: 'block' }}>{n.time}</Typography>
                  </Box>
                  {!n.read && <Box sx={{ width: 8, height: 8, borderRadius: '50%', bgcolor: 'primary.main', mt: 0.75, flexShrink: 0 }} />}
                </Box>
              ))}
            </Box>
            <Box sx={{ px: 2, py: 1, borderTop: '1px solid', borderColor: 'divider', textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'primary.main', fontWeight: 600, cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                onClick={() => { setNotifAnchor(null); navigate('/system/settings/notifications'); }}>
                {t('common:topNav.notificationSettings')}
              </Typography>
            </Box>
          </Popover>
          
          {/* User Profile */}
          <Box 
            sx={{ 
              display: 'flex', 
              alignItems: 'center',
              cursor: 'pointer',
              ml: { xs: 0.5, sm: 1 },
              ...fontFamilyStyle,
              '&:hover': { 
                '& .MuiAvatar-root': {
                  boxShadow: '0 0 0 2px rgba(46, 101, 212, 0.2)'
                }
              }
            }}
            onClick={handleUserMenuOpen}
          >
            <Avatar 
              sx={{ 
                width: 28,
                height: 28,
                fontSize: '0.875rem',
                bgcolor: theme.palette.primary.main,
                transition: 'box-shadow 0.2s ease-in-out',
                ...fontFamilyStyle
              }}
            >
              {user?.avatar_url ? (
                <Box component="img" src={user.avatar_url} alt={displayName} sx={{ width: '100%', height: '100%', objectFit: 'cover' }} />
              ) : (
                avatarInitials
              )}
            </Avatar>
            {!isMobile && (
              <Box sx={{ ml: 1 }}>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    ...typographyStyle,
                    fontWeight: 600
                  }}
                >
                  {displayName}
                </Typography>
              </Box>
            )}
          </Box>
          
          {/* User Profile Menu */}
          <Menu
            anchorEl={userMenuAnchor}
            open={Boolean(userMenuAnchor)}
            onClose={handleUserMenuClose}
            PaperProps={menuPaperProps}
            transformOrigin={{ horizontal: 'right', vertical: 'top' }}
            anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
          >
            <Box sx={{ px: 2, py: 1.5, ...fontFamilyStyle }}>
              <Typography variant="subtitle2" sx={{ ...typographyStyle, fontWeight: 600 }}>
                {displayName}
              </Typography>
              <Typography variant="caption" sx={{ color: 'text.secondary', ...fontFamilyStyle }}>
                {displayEmail}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />
            <MenuItem sx={menuItemStyle} onClick={() => { handleUserMenuClose(); handleSwitchOrg(); }}>
              <SwapHorizOutlined />
              <Typography variant="body2" sx={typographyStyle}>
                {t('common:topNav.switchOrganization')}
              </Typography>
            </MenuItem>
            <Divider sx={{ my: 0.5 }} />
            {/* No standalone "Change password" item here.
                It used to sit beside "Security & 2FA", and once that page gained
                a Password card the same action had two entry points at two
                different depths — one opening a dialog straight from the menu,
                one two clicks deep inside a page. The page is the single home
                for both credentials now, which is why the item below is named
                for the job ("Sign-in & security") rather than for one feature:
                the old "Security & 2FA" label implied the page held only 2FA,
                which is what made a password card in it look misplaced.

                Changing a password is a low-frequency action, so one extra
                click to reach it is the right trade for a single entry point.
                Same menu shape as the worker portal (WorkerLayout). */}
            <MenuItem
              sx={menuItemStyle}
              onClick={() => { handleUserMenuClose(); navigate(securityPath); }}
            >
              <Shield />
              <Typography variant="body2" sx={typographyStyle}>
                {t('common:topNav.security2fa')}
              </Typography>
            </MenuItem>
            {/* Language switcher — inline expand (no nested dropdown) */}
            <MenuItem
              sx={menuItemStyle}
              onClick={() => setLanguageMenuAnchor(languageMenuAnchor ? null : ({} as any))}
            >
              <Language />
              <Typography variant="body2" sx={typographyStyle}>
                {LOCALE_NAMES[i18n.language as SupportedLocale] || 'Language'}
              </Typography>
              <Chip
                label={i18n.language.split('-')[0].toUpperCase()}
                size="small"
                sx={{
                  ml: 'auto',
                  height: 18,
                  fontSize: '0.65rem',
                  borderRadius: 0.75,
                }}
              />
            </MenuItem>
            {languageMenuAnchor && (
              <Box sx={{ pl: 4, maxHeight: 200, overflowY: 'auto' }}>
                {SUPPORTED_LOCALES.map((locale) => (
                  <MenuItem
                    key={locale}
                    selected={i18n.language === locale}
                    onClick={() => {
                      i18n.changeLanguage(locale);
                      document.documentElement.dir = RTL_LOCALES.includes(locale) ? 'rtl' : 'ltr';
                      // Persist to the backend so send-time push/email
                      // render in this worker's own language (Worker.language,
                      // alembic 054). Best-effort — this menu only renders
                      // post-auth, so the call is always authenticated; a
                      // failure must never block the language switch.
                      meApi.updateProfile({ language: locale }).catch(() => {});
                      setLanguageMenuAnchor(null);
                      handleUserMenuClose();
                    }}
                    dense
                    sx={{ fontSize: '0.8rem', minHeight: 32 }}
                  >
                    <Typography variant="body2" sx={{ ...typographyStyle, fontSize: '0.8rem' }}>
                      {LOCALE_NAMES[locale]}
                    </Typography>
                  </MenuItem>
                ))}
              </Box>
            )}
            <Divider sx={{ my: 0.5 }} />
            <MenuItem 
              sx={{ 
                ...menuItemStyle,
                color: 'error.main',
                '& .MuiSvgIcon-root': {
                  color: 'error.main',
                  mr: 1.5,
                }
              }}
              onClick={() => { handleUserMenuClose(); handleSignOut(); }}
            >
              <Logout />
              <Typography variant="body2" sx={typographyStyle}>{t('common:nav.signOut', { defaultValue: 'Sign out' })}</Typography>
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
      {/* Upgrade Dialog */}
      {/* Upgrade Dialog — driven by live data from
          GET /v1/subscription/plans + /v1/subscription/current. The
          old hardcoded 3-card layout (Free / Pro / Enterprise) drifted
          hard from the backend catalog after the Plans seed landed
          (wrong prices, wrong worker caps, no Starter row, "Free"
          labeled as current for every tenant). This version renders
          whatever the backend returns and highlights the caller's
          actual current plan via plan_id match. */}
      <Dialog
        open={upgradeOpen}
        onClose={() => setUpgradeOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 0,
            overflow: 'hidden',
            maxHeight: 'calc(100dvh - 32px)',
            display: 'flex',
            flexDirection: 'column',
          },
        }}
      >
        <Box
          sx={{
            px: 3,
            pt: 3,
            pb: 1.5,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexShrink: 0,
          }}
        >
          <Box>
            <Typography
              variant="h6"
              sx={{ fontWeight: 700, fontSize: '1.25rem', color: 'text.primary' }}
            >
              {t('common:planPicker.title')}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {renderCurrentPlanSummary(plans, currentSub, t)}
            </Typography>
          </Box>
          <IconButton
            size="small"
            onClick={() => setUpgradeOpen(false)}
            aria-label={t('common:actions.close')}
          >
            <CloseOutlined fontSize="small" />
          </IconButton>
        </Box>
        <Box
          sx={{
            px: 3,
            pb: 3,
            pt: 1,
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            overscrollBehavior: 'contain',
            scrollbarGutter: 'stable',
          }}
        >
          {plansLoading && (
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
                gap: 2,
              }}
            >
              {[0, 1, 2, 3].map((i) => (
                <Box
                  key={i}
                  sx={{
                    p: 2.5,
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'divider',
                    minHeight: 320,
                    bgcolor: 'action.hover',
                    opacity: 0.4,
                  }}
                />
              ))}
            </Box>
          )}
          {plansError && !plansLoading && (
            <Box
              sx={{
                p: 3,
                borderRadius: 2,
                border: '1px solid',
                borderColor: 'error.light',
                bgcolor: 'error.light',
                color: 'error.dark',
                textAlign: 'center',
              }}
            >
              <Typography variant="body2" sx={{ mb: 1.5 }}>
                {plansError}
              </Typography>
              <Button
                size="small"
                variant="outlined"
                color="error"
                onClick={() => {
                  setPlans(null);
                  setPlansError(null);
                }}
              >
                {t('common:actions.retry')}
              </Button>
            </Box>
          )}
          {!plansLoading && !plansError && plans && (
            <PlanPicker
              plans={plans}
              currentPlanId={currentSub?.plan_id ?? null}
              activeWorkers={currentSub?.seat_count ?? null}
              currency={currentSub?.billing_currency ?? 'USD'}
              defaultProductLine={upgradeProductLine}
              onSelect={(planId, billingInterval) => {
                setUpgradeOpen(false);
                // The Billing page owns the checkout / change-plan decision
                // (fresh checkout vs. prorated change, and the 409 over-limit
                // resolution flow). Carrying the interval in the URL keeps that
                // single owner rather than duplicating the branch here.
                window.location.href =
                  '/system/billing?action=change_plan&plan_id=' + planId +
                  '&billing_interval=' + billingInterval;
              }}
            />
          )}
        </Box>
      </Dialog>

      {/* About dialog */}
      <GetAppDialog open={getAppOpen} onClose={() => setGetAppOpen(false)} licensedProducts={licensedProducts} />
      <Dialog open={aboutOpen} onClose={() => setAboutOpen(false)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 2 } }}>
        <DialogContent sx={{ pt: 3.5, pb: 2.5}}>
          {/* Logo + tagline are SVDY-specific. Suppressed when the
              tenant is on a white-label-eligible plan
              (HANDOFF_2026-05-16 Addendum 13 / H). White-label
              tenants still get the version block — version info is
              the only content that stays useful for support
              triage regardless of branding. */}
          {!whitelabel && (
            <>
              {/* Logo centered */}
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <img src="https://svdy.com/svdy-logo.png" alt="SVDY" style={{ height: 36, width: 'auto' }} />
              </Box>

              {/* Tagline */}
              <Typography variant="caption" sx={{ color: '#86868b', fontSize: '0.75rem', display: 'block', textAlign: 'center', lineHeight: 1.65, mb: 2.5, px: 1 }}>
                <span dangerouslySetInnerHTML={{ __html: t('common:topNav.aboutTagline') }} />
              </Typography>
            </>
          )}

          {whitelabel && (
            <Typography variant="subtitle2" sx={{ textAlign: 'center', mb: 2.5, fontWeight: 600 }}>
              {t('common:topNav.aboutThisApp')}
            </Typography>
          )}

          {/* Version block */}
          <Stack spacing={0.75} sx={{ mb: 2 }}>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: '#86868b', fontSize: '0.75rem' }}>{t('common:topNav.frontend')}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', fontSize: '0.75rem', fontWeight: 500, color: '#1d1d1f' }}>
                {process.env.REACT_APP_GIT_SHA ?? 'dev'}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: '#86868b', fontSize: '0.75rem' }}>{t('common:topNav.backend')}</Typography>
              <Typography variant="caption" sx={{ fontFamily: 'ui-monospace, SF Mono, Menlo, monospace', fontSize: '0.75rem', fontWeight: 500, color: '#1d1d1f' }}>
                {versionInfo?.commit ?? '—'}
              </Typography>
            </Stack>
            <Stack direction="row" justifyContent="space-between">
              <Typography variant="caption" sx={{ color: '#86868b', fontSize: '0.75rem' }}>{t('common:topNav.built')}</Typography>
              <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#86868b' }}>
                {versionInfo?.built_at ? new Date(versionInfo.built_at).toLocaleDateString(activeLocale()) : '—'}
              </Typography>
            </Stack>
          </Stack>

          {/* Footer link — also SVDY-specific. */}
          {!whitelabel && (
            <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1.5, color: '#86868b', fontSize: '0.7rem' }}>
              <a href="https://svdy.com" target="_blank" rel="noopener noreferrer" style={{ color: '#0071e3', textDecoration: 'none', fontWeight: 500 }}>
                svdy.com
              </a>
              {' · '}
              © 2026 SVDY
            </Typography>
          )}
        </DialogContent>
        <DialogActions sx={{ pb: 2 }}>
          <Button onClick={() => setAboutOpen(false)} fullWidth variant="outlined" sx={{ textTransform: 'none', borderRadius: 1 }}>{t('common:actions.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Global support-case dialog. Listens for the
          `svdy:support:open` window event so any component (e.g.
          OrgSwitcher's "request deletion" helper) can open it
          without prop drilling. */}
      {/* The shared dialog, not a local copy. TopNav carried its own full
          implementation (~130 lines) alongside components/security/
          ChangePasswordDialog.tsx, so the password hint — and the validation
          behind it — had to be maintained twice and had already drifted.
          One implementation means the requirements checklist exists once. */}
      <ChangePasswordDialog
        open={changePasswordOpen}
        onClose={() => setChangePasswordOpen(false)}
      />

      <SupportCaseDialog />

      {/* P1.5a-3 — language switcher coming-soon toast */}
      <Snackbar
        open={!!languageToast}
        autoHideDuration={5000}
        onClose={() => setLanguageToast(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity="info"
          variant="outlined"
          onClose={() => setLanguageToast(null)}
          sx={{ bgcolor: '#fff' }}
        >
          {languageToast}
        </Alert>
      </Snackbar>
    </AppBar>
  );
};



/**
 * Dialog sub-header sentence. Surfaces the caller's current plan by
 * name so they don't have to guess.
 *
 * Reads `sub.plan` directly now that svdy-platform eager-loads the
 * relationship on `/v1/subscription/current` (backend b56747e). The
 * `plans.find` fallback is kept as defensive coverage for the edge
 * case where the subscription references a plan we filtered out of
 * the catalog (e.g. a soft-deleted plan) — normal traffic never hits
 * it.
 */
function renderCurrentPlanSummary(
  plans: Plan[] | null,
  sub: Subscription | null,
  t: (key: string, opts?: any) => string,
): React.ReactNode {
  if (!sub) {
    return t('common:topNav.pickAPlan');
  }
  const currentPlan =
    sub.plan ?? plans?.find((p) => p.id === sub.plan_id) ?? null;
  if (!currentPlan) {
    return t('common:topNav.pickAPlan');
  }
  return (
    <span dangerouslySetInnerHTML={{ __html: t('common:topNav.currentPlanSummary', { planName: currentPlan.name, status: t('common:topNav.planStatus.' + sub.status) }) }} />
  );
}

export default TopNav;

// ---------------------------------------------------------------------------
// Inline Change Password dialog — calls svdy-authorization directly
// ---------------------------------------------------------------------------
